import { useState } from "react";
import { CalendarPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectInstructorPreferences } from "./instructor-preferences-slice";
import {
  backfillCalendarInvites,
  saveSchedulePreferences,
} from "./slice-extensions/instructor-preferences-slice-extension";
import {
  showSuccessNotification,
  showErrorNotification,
} from "../../utils/notifications";

/**
 * One-click shortcut to the global calendar-invites preference, shown on the
 * session create/edit screen while the preference is off. Enabling it here is
 * the same as enabling it in Settings: all upcoming sessions are sent to the
 * instructor's calendar and kept updated. Renders nothing once enabled.
 */
const CalendarInvitesShortcut = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preferences = useAppSelector(selectInstructorPreferences);
  const [busy, setBusy] = useState(false);

  if (!preferences || preferences.calendarInvitesEnabled) {
    return null;
  }

  const enable = async () => {
    setBusy(true);
    try {
      const result = await dispatch(
        saveSchedulePreferences({ ...preferences, calendarInvitesEnabled: true })
      );
      if (!saveSchedulePreferences.fulfilled.match(result)) {
        showErrorNotification(t("schedulePreferences.saveFailed"));
        return;
      }
      const backfill = await dispatch(backfillCalendarInvites());
      showSuccessNotification(
        backfillCalendarInvites.fulfilled.match(backfill) && backfill.payload > 0
          ? t("schedulePreferences.sessionsSent", { count: backfill.payload })
          : t("schedulePreferences.invitesEnabled")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-start gap-2 rounded-lg bg-grey-95 dark:bg-primary-20 px-3 py-2 text-sm text-grey-30 dark:text-grey-80">
      <CalendarPlus size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
      <span>
        {t("sessionForm.calendarInvitesHint")}{" "}
        <button
          type="button"
          className="font-medium text-primary-40 dark:text-primary-90 hover:underline disabled:opacity-50"
          onClick={enable}
          disabled={busy}
        >
          {busy ? t("schedulePreferences.saving") : t("sessionForm.calendarInvitesAction")}
        </button>
      </span>
    </div>
  );
};

export default CalendarInvitesShortcut;
